import { ApiFactory } from '../api/easypos-api';
import { UserProfile } from '../datatypes/user-profile';

// Google sign-in via the OAuth 2.0 authorization-code + PKCE redirect flow.
//
// Why a full-page redirect instead of GSI One Tap: One Tap (and FedCM) is
// unreliable in iOS Safari and installed PWAs — FedCM is Chromium-only, and the
// One Tap iframe needs third-party cookies that iOS ITP blocks and a standalone
// PWA's isolated storage hides. A whole-page redirect to accounts.google.com
// has none of those constraints and works the same everywhere.
//
// Flow: startGoogleLogin() (login page) -> Google consent -> redirect back to
// /auth/google/callback -> completeGoogleLogin() (callback page) exchanges the
// code via the backend (which holds the client secret) for the EasyPos token.

const CLIENT_ID =
  '1020681910887-lgjq9io0t0dl25udonlj9b26ppimj5ob.apps.googleusercontent.com';
const AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';
// PKCE verifier + CSRF state live in sessionStorage for the round-trip to
// Google (same tab), and are cleared the moment the callback consumes them.
const STATE_KEY = 'google-oauth-state';
const VERIFIER_KEY = 'google-oauth-verifier';

export type GoogleLoginResult = { ok: true } | { ok: false; error: string };

export class GoogleAuth {
  // The exact redirect_uri sent to Google and posted back for the exchange —
  // they must match each other and a URI registered on the OAuth client. Built
  // from the live origin + Vite base so it adapts per environment.
  static googleRedirectUri(): string {
    const base: string = import.meta.env.BASE_URL;
    return `${location.origin}${base}auth/google/callback`;
  }

  // Step 1 (login page): stash PKCE verifier + state, then navigate the whole
  // page to Google's consent screen.
  static async startGoogleLogin(): Promise<void> {
    const verifier = randomUrlSafe(32);
    const challenge = await sha256Base64Url(verifier);
    const state = randomUrlSafe(16);
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(STATE_KEY, state);

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: GoogleAuth.googleRedirectUri(),
      response_type: 'code',
      scope: 'openid email profile',
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      prompt: 'select_account',
    });
    location.assign(`${AUTH_ENDPOINT}?${params.toString()}`);
  }

  // Step 2 (callback page): validate the returned state, exchange the code via
  // the backend, build the profile, and announce login. On success, app-index's
  // user-profile-set handler does the routing; the callback page renders any
  // returned error.
  static async completeGoogleLogin(): Promise<GoogleLoginResult> {
    const query = new URLSearchParams(location.search);
    const oauthError = query.get('error');
    if (oauthError) {
      GoogleAuth.clearPkce();
      return {
        ok: false,
        error:
          oauthError === 'access_denied'
            ? 'Sign-in was cancelled.'
            : `Google sign-in error: ${oauthError}`,
      };
    }

    const code = query.get('code');
    const returnedState = query.get('state');
    const expectedState = sessionStorage.getItem(STATE_KEY);
    const verifier = sessionStorage.getItem(VERIFIER_KEY);
    GoogleAuth.clearPkce();

    if (
      !code ||
      !returnedState ||
      !expectedState ||
      returnedState !== expectedState
    ) {
      return {
        ok: false,
        error: 'Sign-in could not be verified. Please try again.',
      };
    }
    if (!verifier) {
      return { ok: false, error: 'Sign-in session expired. Please try again.' };
    }

    const api = ApiFactory.anonymous();
    const res = await api.loginWithGoogleCode({
      code,
      redirectUri: GoogleAuth.googleRedirectUri(),
      codeVerifier: verifier,
    });
    if (!res.success || !res.data) {
      return { ok: false, error: res.error ?? 'Google sign-in failed.' };
    }

    const easyposToken = res.data;
    const base64data = (await api.myProfilePicture()).data ?? undefined;
    const userProfile = new UserProfile({
      id: easyposToken.id,
      displayName: easyposToken.displayName,
      emailAddress: easyposToken.emailAddress ?? undefined,
      authProvider: 'google',
      profileImageBase64: base64data,
      accessToken: easyposToken,
    });

    // Same window event the email-login path uses so app-index's
    // _onUserProfileSet runs the full post-login flow (persist, restore active
    // shop, route off login, connect realtime, join shop group).
    window.dispatchEvent(
      new CustomEvent<UserProfile>('user-profile-set', { detail: userProfile })
    );
    return { ok: true };
  }

  private static clearPkce(): void {
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
  }
}

function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}
